import trim from 'lodash/trim';
import isEmail from 'validator/lib/isEmail';
import isEmpty from 'validator/lib/isEmpty';
import isLength from 'validator/lib/isLength';

import type { CompanySize } from 'src/data/company';
import type { Entity, ID } from 'src/lib/collection';
import type {
  BackendUser,
  BackendUserInviteStatusEnum,
  BackendUserSettings,
} from 'src/types/backend';

export interface User extends Entity {
  email: string;
  name?: string;
  customers?: CustomerUserRole[];
  companyInformation?: CompanyInformation;
  country?: string;
  isKeywee?: boolean;
  isConfirmed?: boolean;
  settings?: UserSettings;
  createdAt: string;
}

export interface CustomerUserRole extends Entity {
  name: string;
  role: UserRole;
  isInvited?: boolean;
}

interface CompanyInformation {
  company_size?: string;
  user_roles?: UserRole[];
}

interface ValidationResult {
  valid: boolean;
  messages: string[];
}

export type UserSettings = BackendUserSettings;

const MAX_NAME_LENGTH = 60;

export const SOCIAL_AUTH_STATE = {
  authorized: 'authorized',
  unauthorized: 'unauthorized',
  tokenIrrelevant: 'tokenIrrelevant',
} as const;
export type SocialAuthState = (typeof SOCIAL_AUTH_STATE)[keyof typeof SOCIAL_AUTH_STATE];

export const validateEmail = (email: string): ValidationResult => {
  const messages = [];

  if (isEmpty(trim(email))) {
    messages.push('Please enter an email address');
  } else if (!isEmail(trim(email))) {
    messages.push('Email is invalid');
  }

  return {
    valid: messages.length === 0,
    messages,
  };
};

export const validateName = (name: string): ValidationResult => {
  const messages = [];

  if (name.match(/[^A-Za-z0-9&.,\-\s]/gi)) {
    messages.push('Only English characters are allowed');
  } else if (!isLength(name, { max: MAX_NAME_LENGTH })) {
    messages.push(`Names are limited to ${MAX_NAME_LENGTH} characters.`);
  }

  return {
    valid: messages.length === 0,
    messages,
  };
};

export const MARKETING_USER_POSITION = {
  other: 'Other',
  contentMarketing: 'Content Marketing',
  demandGenAcquisition: 'Demand Gen/Acquisition',
  socialMediaManager: 'Social Media Manager',
  productMarketing: 'Product Marketing',
  marketingOps: 'Marketing Ops',
  vpMarketing: 'VP Marketing',
  cmo: 'CMO',
  founderCeo: 'Founder/CEO',
} as const;
export type MarketingUserPosition =
  (typeof MARKETING_USER_POSITION)[keyof typeof MARKETING_USER_POSITION];

// Currently, almost all the "enum values" equal "visible labels"
export const getUserMarketingPositionLabel = (position: MarketingUserPosition): string => {
  return position;
};

export const USER_ROLE = {
  admin: 'admin',
  member: 'member',
  reviewer: 'reviewer',
} as const;
export type UserRole = (typeof USER_ROLE)[keyof typeof USER_ROLE];

export const USER_INVITE_STATUS = {
  pending: 'pending',
  accepted: 'accepted',
  discarded: 'discarded',
} as const satisfies Record<string, BackendUserInviteStatusEnum>;
export type UserInviteStatus = BackendUserInviteStatusEnum;

// A reviewer is not a user of the account: no seat, and no role changes to or from Reviewer.
export const ACCOUNT_USER_ROLES: readonly UserRole[] = [USER_ROLE.admin, USER_ROLE.member];

const USER_ROLE_LABEL = {
  [USER_ROLE.admin]: 'Admin',
  [USER_ROLE.member]: 'Member',
  [USER_ROLE.reviewer]: 'Reviewer',
};

export const getUserRoleLabel = (role: UserRole): string => {
  return USER_ROLE_LABEL[role] || role;
};

// `me` returns account ids as numbers. The URL and local storage give them as strings.
const findUserCustomer = (user: User, customerId: ID) =>
  user?.customers?.find((customer) => String(customer.id) === String(customerId));

export const getUserRoleForCustomer = (user: User, customerId: ID): UserRole | undefined =>
  findUserCustomer(user, customerId)?.role;

export const isUserInvitedForCustomer = (user: User, customerId: ID): boolean =>
  findUserCustomer(user, customerId)?.isInvited ?? false;

export const isAdminRole = (role?: UserRole): boolean => {
  return role === USER_ROLE.admin;
};

export const isReviewerRole = (role?: UserRole): boolean => {
  return role === USER_ROLE.reviewer;
};

export const withoutReviewers = <T extends { role?: UserRole }>(items: T[]): T[] =>
  items.filter((item) => !isReviewerRole(item.role));

export const getUserName = (user: Pick<User, 'email'> & { name?: string | null }): string => {
  if (user?.name) {
    return user?.name;
  }

  if (user?.email) {
    return user?.email?.split('@')[0];
  }

  return '';
};

// TODO: merge with "getUserName" when types are aligned
export const getBackendUserName = (user: BackendUser): string => {
  if (user?.name) {
    return user?.name;
  }

  if (user?.email) {
    return user?.email?.split('@')[0];
  }

  return '';
};

export const getUserCompanySize = (user: User): CompanySize | undefined =>
  user?.companyInformation?.company_size as CompanySize;

export const getUserCompanyPositions = (user: User): string[] =>
  user?.companyInformation?.user_roles || [];

export const getUserCountry = (user: User): string | undefined => user?.country;

export const isUserAdminForCustomer = (user: User, customerId: ID): boolean => {
  const roleForCustomer = getUserRoleForCustomer(user, customerId);
  return isAdminRole(roleForCustomer);
};

export const isUserReviewerForCustomer = (user: User, customerId: ID): boolean => {
  const roleForCustomer = getUserRoleForCustomer(user, customerId);
  return isReviewerRole(roleForCustomer);
};
